import { prisma, type Prisma } from "client";

type CreateTaxOptions = { whiteLabelId?: string; organizationId?: string };

type TaxItem = {
  id: string;
  region: string;
  taxNumber: string;
  vatNumber: string;
  saleTaxRate: number;
  enable: boolean;
};
//create
export const createTax = async (
  taxBody: Prisma.TaxRegionCreateInput,
  options?: string | CreateTaxOptions
) => {
  const whiteLabelId =
    typeof options === "string" ? options : options?.whiteLabelId;
  const organizationId =
    typeof options === "object" && options ? options.organizationId : undefined;
  try {
    const response = await prisma.taxRegion.create({
      data: {
        ...taxBody,
        whiteLabels: whiteLabelId
          ? { connect: { id: whiteLabelId } }
          : undefined,
        organizations: organizationId
          ? { connect: { id: organizationId } }
          : undefined,
      },
    });
    return response;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

//update
export const updateTax = async (taxBody: any, id: string) => {
  try {
    const response = await prisma.taxRegion.update({
      where: {
        id: id,
      },
      data: {
        ...taxBody,
      },
    });
    return response;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

//get
export const getTaxByWhiteLabel = async (id: string) => {
  const response = await prisma.taxRegion.findMany({
    where: { whiteLabelIds: { hasSome: [id] } },
    include: {
      whiteLabels: true,
    },
  });
  return response;
};

export const getTaxByOrganization = async (organizationId: string) => {
  const response = await prisma.taxRegion.findMany({
    where: { organizationIds: { hasSome: [organizationId] } },
    include: {
      organizations: true,
    },
  });
  return response;
};

/**
 * Merged effective tax list by region: org overrides win over WL for same region.
 * Returns items with source, and whiteLabelHasRegion (true if WL has this region; used to show/hide Reset).
 */
export const getEffectiveTaxForOrg = async (
  whiteLabelId: string,
  organizationId: string
) => {
  const [wlTax, orgTax] = await Promise.all([
    getTaxByWhiteLabel(whiteLabelId),
    getTaxByOrganization(organizationId),
  ]);
  const orgByRegion = new Map(orgTax.map((t) => [t.region, t]));
  const wlRegionSet = new Set(wlTax.map((t) => t.region));

  const merged: Array<{
    item: TaxItem;
    source: "inherited" | "overridden";
    whiteLabelHasRegion: boolean;
  }> = [];
  for (const t of orgTax) {
    merged.push({
      item: t as TaxItem,
      source: "overridden",
      whiteLabelHasRegion: wlRegionSet.has(t.region),
    });
  }
  for (const t of wlTax) {
    if (orgByRegion.has(t.region)) continue;
    merged.push({
      item: t as TaxItem,
      source: "inherited",
      whiteLabelHasRegion: true,
    });
  }
  return merged;
};

export const deleteTax = async (id: string, organizationId?: string) => {
  try {
    if (organizationId) {
      const tax = await prisma.taxRegion.findUnique({
        where: { id },
        select: { organizationIds: true },
      });
      if (
        !tax ||
        !tax.organizationIds ||
        !tax.organizationIds.includes(organizationId)
      ) {
        throw new Error(
          "Tax region not found or not owned by this organization"
        );
      }
    }
    return await prisma.taxRegion.delete({
      where: { id },
    });
  } catch (error: any) {
    throw new Error(error.message);
  }
};
